import * as React from 'react';
import { useCallback, useContext, useState } from 'react';
import { DollarOutlined } from '@ant-design/icons';
import {
  Avatar,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Switch,
} from 'antd';
import * as R from 'ramda';

import {
  AddEditBillingRate,
  FormErrors,
} from '@totem/components/billingRates/dataTypes';
import GenericAutoComplete from '@totem/components/common/autoCompletes/GenericAutoComplete';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { AutoCompleteValue } from '@totem/types/autoComplete';
import { ResponseError } from '@totem/types/error';
import { isNotNull } from '@totem/utilities/common';
import { BILLING_RATES_ENDPOINT } from '@totem/utilities/endpoints';
import logger from '@totem/utilities/logger';

import './addEditDialog.css';
const FormItem = Form.Item;
const styles = {
  form: {
    width: '100%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
};
type ValidationResponse = {
  isValid: boolean;
  errors: FormErrors;
};
type Props = {
  open: boolean;
  loading: boolean;
  billingRateData: AddEditBillingRate;
  onClose: (boolean) => void;
};
const AddEditDialog = ({ open, loading, billingRateData, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [editData, setEditData] = useState<AddEditBillingRate>(billingRateData);
  const isEdit = billingRateData.id !== '';
  const ShowErrorMessage = async (response: Response) => {
    try {
      const errResponse: ResponseError = await response.json();
      if (
        errResponse.developerMessage ===
        'UserController.ValidateEmployee.GetBillingRateByEmail'
      ) {
        notification.error({
          title: 'Duplicate Billing Rate',
          description:
            'This employee already has a billing rate assigned. Please select another employee or update the existing rate.',
          duration: 5,
        });
      } else {
        notification.error({
          title: 'Unable to Save Billing Rate',
          description:
            'An error occurred while saving the billing rate. Please try again or contact support if the problem persists.',
          duration: 5,
        });
      }
    } catch {
      /* empty */
    }
  };

  const CheckResponseCreateShowError = (response: Response) => {
    if (response.status < 400) {
      return true;
    }
    ShowErrorMessage(response);
    return false;
  };

  const sendUpdateBillingRate = useCallback(
    async (payload: AddEditBillingRate) => {
      if (isSending) {
        return;
      }
      setIsSending(true);
      const endPoint =
        payload.id !== ''
          ? `${BILLING_RATES_ENDPOINT}/${payload.id}`
          : `${BILLING_RATES_ENDPOINT}/create`;
      fetch(endPoint, {
        method: payload.id !== '' ? 'PUT' : 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      }).then((res) => {
        //CheckResponseShowError(res);
        CheckResponseCreateShowError(res);

        setIsSending(false);

        onClose(true);
      });
    },
    [isSending, onClose],
  );

  const isValid = (): ValidationResponse => {
    const newErrors: FormErrors = {};
    if (R.isEmpty(editData.name) || R.isNil(editData.name)) {
      newErrors.name = 'Name is required.';
    }
    if (R.isEmpty(editData.email) || R.isNil(editData.email)) {
      newErrors.name = `Select Employee`;
    }
    const emailValue = (editData.email || '').trim();
    if (emailValue.length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailValue) && R.isNil(newErrors.email)) {
        newErrors.email = 'email must be a valid email address.';
      }
    }
    if (R.isEmpty(editData.billingRate) || R.isNil(editData.billingRate)) {
      newErrors.billingRate = `billing rate is required.`;
    }
    if (R.isEmpty(editData.hourlyCost) || R.isNil(editData.hourlyCost)) {
      newErrors.hourlyCost = `hourly cost is required.`;
    }
    if (!R.isNil(editData.billingRate) || !R.isEmpty(editData.billingRate)) {
      if (!Number.isFinite(editData.billingRate) || editData.billingRate < 0) {
        newErrors.billingRate = 'billing rate must be a valid number >= 0.';
      }
    }
    setErrors(newErrors);
    return {
      isValid: R.isEmpty(newErrors),
      errors: newErrors,
    };
  };

  const saveBillingRate = () => {
    const validation = isValid();
    if (!validation.isValid) {
      logger.error('Not Valid');
      return;
    }
    sendUpdateBillingRate(editData);
  };

  const handleNumberChange = (name: string, value: number) => {
    setEditData({ ...editData, [name]: value });
  };

  const closePopup = () => {
    onClose(false);
  };

  const handleEmployeeChange = (selectedEmployee: string) => {
    setEditData({ ...editData, name: selectedEmployee, email: '' });
  };

  const handleEmployeeSelect = (selectedEmployee: AutoCompleteValue) => {
    setEditData({
      ...editData,
      name: selectedEmployee.display,
      email: selectedEmployee.value,
    });
  };

  return (
    <div>
      <Modal
        title={
          billingRateData.id === '' ? 'Add Billing Rate' : 'Update Billing Rate'
        }
        open={open}
        onCancel={() => onClose(false)}
        footer={
          <>
            <Button onClick={() => closePopup()}>{'Cancel'}</Button>
            <Button type="primary" onClick={saveBillingRate} loading={loading}>
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <div styleName="modal-container">
          <>
            <div styleName="avatar-container">
              <Avatar size="large" icon={<DollarOutlined />} shape="circle" />
            </div>
            <Form layout="vertical" style={styles.form}>
              <FormItem
                label="Name"
                colon={false}
                validateStatus={R.isNil(errors.name) ? 'success' : 'error'}
                help={R.isNil(errors.name) ? null : errors.name}
                style={styles.formItem}
              >
                <GenericAutoComplete
                  type={'Employee'}
                  label={''}
                  limit={15}
                  onSelect={handleEmployeeSelect}
                  onChange={handleEmployeeChange}
                  allowClear
                  allowRefresh
                  allowFreeform={false}
                  selectedValue={
                    isNotNull(editData)
                      ? {
                          display: editData.name,
                          value: editData.email,
                        }
                      : { display: '', value: '' }
                  }
                />
              </FormItem>
              <FormItem
                label="Department"
                colon={false}
                style={styles.formItem}
              >
                <Input
                  style={{ width: '100%' }}
                  value={editData.department}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setEditData((prev) => ({
                      ...prev,
                      department: event.target.value,
                    }))
                  }
                />
              </FormItem>
              <FormItem
                label="Department Lead"
                colon={false}
                style={styles.formItem}
              >
                <Switch
                  value={editData.departmentLead}
                  onChange={(checked: boolean) => {
                    setEditData((prev) => ({
                      ...prev,
                      departmentLead: checked,
                    }));
                  }}
                />
              </FormItem>
              <FormItem
                label="Billing Rate"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  value={editData.billingRate}
                  onChange={(newValue: number) =>
                    handleNumberChange('billingRate', newValue)
                  }
                  min={0}
                />
              </FormItem>
              <FormItem
                label="Hourly Cost"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  value={editData.hourlyCost}
                  onChange={(newValue: number) =>
                    handleNumberChange('hourlyCost', newValue)
                  }
                  min={0}
                />
              </FormItem>
              <FormItem
                label="Available Hours / Week"
                colon={false}
                validateStatus="success"
                style={styles.formItem}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  value={editData.availableHours}
                  onChange={(newValue: number) =>
                    handleNumberChange('availableHours', newValue)
                  }
                  min={0}
                />
              </FormItem>
            </Form>
          </>
        </div>
      </Modal>
    </div>
  );
};
export default AddEditDialog;
